import ProcessFlowBuilderModel from "~/models/tracked/processFlowBuilderModel";
import {
  ProcessFlowMappedVariableType,
} from "../../../../formability/processFlowBuilder/enums/processFlowMappedVariableType";
import {ReferencePath} from "~/models/referencePaths/referencePath";
import {RelationType} from "~/models/relationType";
import {Hash} from "~/interfaces/hashInterfaces";
import ElementHandler from "~/services/elementHandler";
import IRepo from '~/interfaces/repo'
import Repo from '~/state/modules/repo'
import {IProcessFlowMappedVariable, IProcessFlowVariable, IProcessFlowVariableMap} from '~/interfaces/processFlowInterfaces'
import ProcessFlowMappedVariableDefinition, {ProcessFlowMappedVariableClasses} from '~/models/processFlows/processFlowMappedVariableDefinition'
import {IReferencePath, IReferencePathNodeSpecific} from '~/interfaces/referencePathInterfaces'
import {ReferenceNodeType} from '~/enums/referenceNodeType'
import {GenericType} from '~/interfaces/genericTypeInterfaces'
import {Logger} from "~/services/logger";
import {BooleanHelper} from '~/helpers/booleanHelper'
import {DatetimeMode} from '~/models/datetimeMode'
import {TargetType} from '~/models/conditionalRules/targetType'
import {ComparisonType} from '~/models/conditionalRules/comparisonType'
import FormElement from '../../../../formability/form_builder/models/formElements/formElement'
import ObjectModelProperty from '~/models/objectModelProperties/objectModelProperty'
import Formula from '~/models/formula'
import {ObjectHelper} from '~/helpers/objectHelper'
import {ArrayHelper} from '~/helpers/arrayHelper'
import {ModelType} from '~/enums/modelType'
import Guid from '~/helpers/guid'
import { InstanceHelper } from '~/helpers/instanceHelper'

type ConditionalReferenceTypes = ObjectModelProperty | FormElement | ReferenceNodeType

export default class ProcessFlowMappedVariable extends ProcessFlowBuilderModel implements IProcessFlowMappedVariable {
  source_type: ProcessFlowMappedVariableType
  source_variable_path: ReferencePath; // refers to MappedVariable Var, not ProcessFlowVariable.
  source_constant: string | string[]
  source_constant_type: GenericType
  source_constant_path: ReferencePath;
  source_formula: string
  formula_reference_paths: Hash<ReferencePath> = {}
  source_alias: string
  destination_variable_client_id: string //optional; refers to ProcessFlowVariable, not MappedVariable Var.

  _formulaReferencePathsHandler: ElementHandler<ReferencePath> = new ElementHandler<ReferencePath>(this, ReferencePath, 'formula_reference_paths')
  get formulaReferencePathsHandler(): ElementHandler<ReferencePath> {
    return this._formulaReferencePathsHandler
  }

  constructor(protected _rootName: string, json: Partial<IProcessFlowMappedVariable> = {}, opts: {source_type: ProcessFlowMappedVariableType} = {source_type: ProcessFlowMappedVariableType.Variable}) {
    super()
    // Defaults
    this.defineClientId(json.client_id);
    this.defineProperty<this>('source_type', opts.source_type)
    this.defineProperty<this>('source_constant', null)
    this.defineProperty<this>('source_constant_type', ModelType.Text)
    this.defineProperty<this>('source_formula', null)
    this.defineProperty<this>('source_alias', null)
    this.defineProperty<this>('destination_variable_client_id', null)
    // needed so undo redo knows how to treat component (see TrackedModel reference: isCollectionRoot)
    this.createEmbeddedObject = ProcessFlowMappedVariableDefinition.createEmbeddedDefinitionFactory(this)
  }
  // ******************** RELATIONS AND REPOS ******************** //
  static repo: IRepo<ProcessFlowMappedVariable> = new Repo<ProcessFlowMappedVariable>('processFlowMappedVariables', ProcessFlowMappedVariable, {});
  repo: IRepo<this>;

  addToRepo(): void {
    ProcessFlowMappedVariable.repo.add(this)
  }

  get rootName(): string {
    return this._rootName
  }

  get isCollectionRoot(): boolean {
    if (BooleanHelper.isStrictBoolean(this._isCollectionRoot)) {
      return this._isCollectionRoot
    } else {
      return this._isCollectionRoot = this.parent.EMBEDDED_MODELS[this.rootName].relationType !== RelationType.singleObject;
    }
  }

  get className(): ProcessFlowMappedVariableClasses {
    return 'ProcessFlowMappedVariable'
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return ProcessFlowMappedVariableDefinition.embeddedModelDefinitionFactory('ProcessFlowMappedVariable')
  }

  initializeEmbedded(parent, item: IReferencePath, position: number = null, json: object = {}, selectItem: boolean = true) {
    item.attachToParent(parent)
    return item
  }

  /**
   * create an empty source_variable_path
   */
  initializeSourceVariablePath(): void {
    if (!this.source_variable_path) {
      const refPath = this.createEmbeddedObject.source_variable_path(null, {client_id: Guid.create()})
      this.initializeEmbedded(this, refPath)
    }
  }

  // ******************** RELATIONS AND REPOS END ******************** //
  // @see ProcessFlowMappedVariableDefinition.createEmbeddedDefinitionFactory
  declare createEmbeddedObject: {
    source_variable_path: (parent, json: Hash) => IReferencePath
    formula_reference_paths: (parent, json: Hash) => IReferencePath
    source_constant_path: (parent, json: Hash) => IReferencePath
  }

  /**
   * Remove any ReferencePaths that are not referenced by something (like a formula)
   */
  cleanUpPaths(): void {
    return ProcessFlowMappedVariableDefinition.cleanUpPaths(this)
  }

  get pathExists(): boolean {
    return ProcessFlowMappedVariableDefinition.pathExists(this)
  }

  get variableExists(): boolean {
    return ProcessFlowMappedVariableDefinition.variableExists(this)
  }

  get allReferencesLinked(): boolean {
    return ProcessFlowMappedVariableDefinition.allReferencesLinked(this)
  }

  get valueReferenceVariableTypes(): readonly ProcessFlowMappedVariableType[] {
    return ProcessFlowMappedVariableDefinition.valueReferenceVariableTypesFor(this.source_variable_path?.lastNode as IReferencePathNodeSpecific)
  }

  get sourceVariable(): IProcessFlowVariable {
    const firstNode = this.source_variable_path?.nodesHandler?.first
    if (firstNode?.reference_type === ReferenceNodeType.ProcessFlowVariable) {
      return firstNode.reference()
    }
  }

  get destinationVariable(): IProcessFlowVariable {
    if (this.destination_variable_client_id) {
      return this.processFlow?.variablesHandler.find(this.destination_variable_client_id)
    }
  }

  get hasSource(): boolean {
    switch (this.source_type) {
      case ProcessFlowMappedVariableType.Constant:
        return this.isConstantPresent;
      case ProcessFlowMappedVariableType.Formula:
        return !!this.source_formula;
      case ProcessFlowMappedVariableType.Variable:
        return this.source_variable_path?.nodesArray?.length > 0;
      default:
        Logger.errorOnce('ProcessFlowMappedVariable#hasSource', 'Unexpected :source_type.')
    }
  }

  // ******************** IConditionalRuleReferencedControl START ******************** //
  isValidForConditionalLogic: boolean = true;
  // interface methods unused as query builder uses MappedVariable value instead of input/datepicker/etc.
  getChoiceComparisonOptions(): any[] { return null }
  getConditionDateTimeMode(): DatetimeMode { return null };
  getConditionMinDateTime(): string { return null };
  getConditionMaxDateTime(): string { return null };

  get referenceNameOrDefault(): string {
    switch (this.source_type) {
      case ProcessFlowMappedVariableType.Variable:
        return this.source_variable_path?.dynamicFieldName;
      case ProcessFlowMappedVariableType.Constant:
        if (Array.isArray(this.source_constant)) {
          let val: string[]
          if(this.source_constant_path) {
            val = this.source_constant_path.lastNodeService.toUniversalValue(this.source_constant)
          } else {
            val = this.source_constant
          }
          return ArrayHelper.toSentence(val)
        } else {
          if (this.source_constant_path?.isDateTime) {
            return this.source_constant_path.lastNodeService.toDisplayValue(this.source_constant, { timezone: this.organization.time_zone })
          } else {
            return this.source_constant?.toString();
          }
        }
      case ProcessFlowMappedVariableType.Formula:
        return Formula.replaceVariables(this.source_formula, clientId => {
          return this.formulaReferencePathsHandler.find(clientId)?.dynamicFieldName
        })
    }
  };

  displayConditionType(targetType: TargetType, resetReferencedControl: boolean = false): ComparisonType {
    // referenceControl MappedVariable can only have source_type: ProcessFlowMappedVariableType.Variable;
    if (this.hasSource) {
      const reference = this.source_variable_path.lastNode.service.reference();
      if (ObjectHelper.hasProperty(reference, 'displayConditionType')) {
        return reference.displayConditionType(targetType, resetReferencedControl);
      } else {
        switch (reference) {
          case ReferenceNodeType.OriginalSubmitter:
          case ReferenceNodeType.RecordHolder:
          case ReferenceNodeType.FormSubmissionID:
            return ComparisonType.text;
          case ReferenceNodeType.FormSubmissionDate: // intentional fall-through
          case ReferenceNodeType.WorkflowStepStart: // intentional fall-through
          case ReferenceNodeType.WorkflowStepEnd:
            return ComparisonType.datetime;
        }
      }
    }
  }

  targetTypes(): TargetType[] {
    // referenceControl MappedVariable can only have source_type: ProcessFlowMappedVariableType.Variable;
    if (this.hasSource) {
      const reference: ConditionalReferenceTypes = <ConditionalReferenceTypes>this.source_variable_path.lastNode.service.reference();
      if (reference instanceof FormElement || reference instanceof ObjectModelProperty) {
        return reference.targetTypes();
      }
    }
    return [];
  }
  // ******************** IConditionalRuleReferencedControl END ******************** //

  /**
   *
   * The +source_constant+ is something like the client_id of a pick list option but the back end needs to know how
   * to find that option. Create a +source_constant_path+ as instructions to get to the pick list.
   *
   * Take in a VariableMap or another MappedVariable and "clone" a path
   * For VariableMap, clone its +variable_reference+'s path
   */
  setConstantVariablePath(destinationValue: IProcessFlowMappedVariable | IProcessFlowVariableMap): void {
    let destinationMappedVariable: IProcessFlowMappedVariable
    if ('variable_reference' in destinationValue) {
      destinationMappedVariable = destinationValue.variable_reference
    } else {
      destinationMappedVariable = destinationValue
    }

    const path = this.createEmbeddedObject.source_constant_path(
      this,
      ObjectHelper.cloneWithNewClientIds(destinationMappedVariable.source_variable_path.asJsonForLoad()),
    )
    this.initializeEmbedded(this, path)
  }

  /**
   * So data is saved on back end as a number instead of string
   */
  setConstantType(type: GenericType) {
    this.source_constant_type = type
  }

  /**
   * booleans must be handled specially so +false+ can be considered "present"
   */
  get isConstantPresent(): boolean {
    switch (this.source_constant_type) {
      case ModelType.Boolean:
        return BooleanHelper.isStrictBoolean(this.source_constant)
      default:
        return InstanceHelper.isPresent(this.source_constant)
    }
  }
}